import React from 'react';
import { Shield, CheckCircle, AlertTriangle, Lock, Users, TrendingUp, FileText, Award, ExternalLink } from 'lucide-react';

const Information = () => {
  return (
    <div className="min-h-screen bg-dark-bg text-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-dark-card border border-purple-accent/40 rounded-lg p-8 mb-6 relative overflow-hidden"
             style={{ boxShadow: '0 0 40px rgba(178, 75, 243, 0.2)' }}>
          <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex flex-col items-center justify-center text-white font-bold text-xs text-center leading-tight transform -rotate-12 animate-pulse"
               style={{ boxShadow: '0 0 30px rgba(255, 215, 0, 0.4)' }}>
            <Award className="mb-1" size={24} />
            <div>APPROVED</div>
            <div>AI IMPLEMENTATION</div>
            <div className="text-[10px] mt-1">AIO-213</div>
          </div>
          
          <div className="flex items-center space-x-4 mb-4">
            <img 
              src="/vision-logo.png" 
              alt="VISION Logo" 
              className="h-16 w-16 object-contain"
              style={{ filter: 'drop-shadow(0 0 20px #b24bf3)' }}
            />
            <div>
              <h1 className="text-4xl font-bold text-cyan-accent mb-2"
                  style={{ textShadow: '0 0 20px #00e5ff' }}>
                VISION
              </h1>
              <p className="text-xl text-purple-accent"
                 style={{ textShadow: '0 0 10px #b24bf3' }}>
                Validation & Insight System for Intelligent Operations & Notifications
              </p>
            </div>
          </div>
        </div>

        {/* Internal Development & Certification */}
        <div className="bg-dark-card border border-cyan-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.15)' }}>
          <h2 className="text-2xl font-bold text-cyan-accent mb-4 flex items-center gap-2">
            <Award size={24} />
            Internal Development & Certification
          </h2>
          <p className="text-gray-300 mb-4">
            This application has been designed and developed internally by EchoStar's IT team in collaboration with our AI Governance and Change Management teams. Project VISION represents a responsible approach to AI implementation that prioritizes human oversight, transparency, and operational safety.
          </p>
          <div className="bg-purple-accent/10 border-l-4 border-purple-accent rounded p-4">
            <p className="text-purple-accent font-semibold">
              <strong>Certification:</strong> This AI implementation has been thoroughly reviewed and approved by EchoStar's AI Governance Committee and meets all internal standards for responsible AI deployment in production environments.
            </p>
          </div>
        </div>

        {/* System Capabilities & Limitations */}
        <div className="bg-dark-card border border-orange-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(255, 107, 53, 0.15)' }}>
          <h2 className="text-2xl font-bold text-orange-accent mb-4 flex items-center gap-2">
            <AlertTriangle size={24} />
            System Capabilities & Limitations
          </h2>
          <p className="text-gray-300 mb-6">
            Project VISION operates within clearly defined boundaries to ensure safe and responsible AI implementation:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <h3 className="text-cyan-accent font-bold mb-2 flex items-center gap-2">
                <FileText size={18} />
                Policy-Based Review
              </h3>
              <p className="text-gray-300 text-sm">
                Reviews and comments on changes based on EchoStar policies and best practices
              </p>
            </div>
            
            <div className="bg-orange-accent/10 border border-orange-accent/40 rounded-lg p-4">
              <h3 className="text-orange-accent font-bold mb-2 flex items-center gap-2">
                <Shield size={18} />
                Advisory Role Only
              </h3>
              <p className="text-gray-300 text-sm">
                Does not approve, reject, or alter any change requests - humans maintain full decision-making authority
              </p>
            </div>
            
            <div className="bg-lime-accent/10 border border-lime-accent/40 rounded-lg p-4">
              <h3 className="text-lime-accent font-bold mb-2 flex items-center gap-2">
                <FileText size={18} />
                Work Notes Integration
              </h3>
              <p className="text-gray-300 text-sm">
                Places comments exclusively in the work notes section of change requests for transparency
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
              <h3 className="text-red-500 font-bold mb-2 flex items-center gap-2">
                <AlertTriangle size={18} />
                Automated Incident Management
              </h3>
              <p className="text-gray-300 text-sm">
                Creates incidents when monitoring systems indicate a configuration item has not reached healthy state post-implementation
              </p>
            </div>
          </div>
          
          <div className="bg-orange-accent/10 border-l-4 border-orange-accent rounded p-4">
            <p className="text-orange-accent font-semibold">
              <strong>Important:</strong> This system augments human decision-making but never replaces human judgment in critical change management processes.
            </p>
          </div>
        </div>

        {/* AI Ethics & Principles */}
        <div className="bg-dark-card border border-purple-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(178, 75, 243, 0.15)' }}>
          <h2 className="text-2xl font-bold text-purple-accent mb-4 flex items-center gap-2">
            <CheckCircle size={24} />
            AI Ethics & Principles
          </h2>
          <p className="text-gray-300 mb-6">
            Project VISION is built on core ethical principles that ensure responsible AI deployment:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <h3 className="text-cyan-accent font-bold mb-2">Human Oversight Preservation</h3>
              <p className="text-gray-300 text-sm">
                The system explicitly maintains humans as final decision-makers for change approvals, with AI serving only as coaching and validation support.
              </p>
            </div>
            
            <div className="bg-lime-accent/10 border border-lime-accent/40 rounded-lg p-4">
              <h3 className="text-lime-accent font-bold mb-2">Explainable AI Outputs</h3>
              <p className="text-gray-300 text-sm">
                Claude Sonnet provides clear rationale and evidence links for all pass/warn/fail decisions, ensuring transparency.
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
              <h3 className="text-red-500 font-bold mb-2">Immutable Audit Trail</h3>
              <p className="text-gray-300 text-sm">
                Complete logging of all automated decisions and human overrides enables bias detection and algorithmic accountability.
              </p>
            </div>
            
            <div className="bg-orange-accent/10 border border-orange-accent/40 rounded-lg p-4">
              <h3 className="text-orange-accent font-bold mb-2">Team-Agnostic Routing</h3>
              <p className="text-gray-300 text-sm">
                Incidents are automatically assigned based on objective CI ownership data rather than subjective assignment patterns.
              </p>
            </div>
          </div>
          
          <div className="bg-purple-accent/10 border-l-4 border-purple-accent rounded p-4">
            <p className="text-purple-accent font-semibold">
              <strong>Key Principle:</strong> AI augments human expertise rather than replacing human judgment in critical IT operations.
            </p>
          </div>
        </div>

        {/* Compliance & Governance */}
        <div className="bg-dark-card border border-cyan-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.15)' }}>
          <h2 className="text-2xl font-bold text-cyan-accent mb-4 flex items-center gap-2">
            <Shield size={24} />
            Compliance & Governance
          </h2>
          <p className="text-gray-300 mb-6">
            Project VISION's AI-driven change validation system adheres to multiple industry standards:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-cyan-accent" />
                <h3 className="text-cyan-accent font-bold">SOC 2 Type II</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">Security & Availability</p>
              <p className="text-gray-300 text-sm">
                Comprehensive security controls, audit logging, and access management via Okta/OAuth2.
              </p>
            </div>
            
            <div className="bg-lime-accent/10 border border-lime-accent/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-lime-accent" />
                <h3 className="text-lime-accent font-bold">GDPR/CCPA</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">Data Privacy & Protection</p>
              <p className="text-gray-300 text-sm">
                Processing only non-sensitive metadata with strict data handling protocols.
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-red-500" />
                <h3 className="text-red-500 font-bold">PCI DSS</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">Payment Card Security</p>
              <p className="text-gray-300 text-sm">
                End-to-end encryption and secure API integrations.
              </p>
            </div>
            
            <div className="bg-orange-accent/10 border border-orange-accent/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-orange-accent" />
                <h3 className="text-orange-accent font-bold">ISO 27001</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">Information Security Management</p>
              <p className="text-gray-300 text-sm">
                Risk-based security framework and role-based access controls.
              </p>
            </div>
            
            <div className="bg-purple-accent/10 border border-purple-accent/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-purple-accent" />
                <h3 className="text-purple-accent font-bold">ISO 9001</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">Quality Management System</p>
              <p className="text-gray-300 text-sm">
                Quality management principles embedded in continuous monitoring.
              </p>
            </div>
            
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-cyan-accent" />
                <h3 className="text-cyan-accent font-bold">ITIL</h3>
              </div>
              <p className="text-gray-400 text-xs mb-2">IT Service Management</p>
              <p className="text-gray-300 text-sm">
                Seamless integration with existing change management processes.
              </p>
            </div>
          </div>
        </div>

        {/* Data Privacy & Security */}
        <div className="bg-dark-card border border-lime-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(178, 255, 89, 0.15)' }}>
          <h2 className="text-2xl font-bold text-lime-accent mb-4 flex items-center gap-2">
            <Lock size={24} />
            Data Privacy & Security
          </h2>
          <p className="text-gray-300 mb-6">
            We implement robust data protection measures to safeguard sensitive information:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <h3 className="text-cyan-accent font-bold mb-2">Minimal Data Collection</h3>
              <p className="text-gray-300 text-sm">
                Only non-sensitive metadata is processed by AI systems, ensuring zero personal data exposure.
              </p>
            </div>
            
            <div className="bg-lime-accent/10 border border-lime-accent/40 rounded-lg p-4">
              <h3 className="text-lime-accent font-bold mb-2">End-to-End Encryption</h3>
              <p className="text-gray-300 text-sm">
                All data transmission uses industry-standard 256-bit encryption.
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
              <h3 className="text-red-500 font-bold mb-2">Access Controls</h3>
              <p className="text-gray-300 text-sm">
                Role-based access via Okta/OAuth2 with principle of least privilege.
              </p>
            </div>
            
            <div className="bg-orange-accent/10 border border-orange-accent/40 rounded-lg p-4">
              <h3 className="text-orange-accent font-bold mb-2">Data Retention</h3>
              <p className="text-gray-300 text-sm">
                Automated purging of evaluation data according to retention policies.
              </p>
            </div>
          </div>
        </div>

        {/* AI Technology Platform */}
        <div className="bg-dark-card border border-purple-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(178, 75, 243, 0.15)' }}>
          <h2 className="text-2xl font-bold text-purple-accent mb-4 flex items-center gap-2">
            <Users size={24} />
            AI Technology Platform
          </h2>
          <p className="text-gray-300 mb-6">
            Project VISION leverages two advanced AI models working in tandem:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg p-4">
              <h3 className="text-cyan-accent font-bold mb-2">Claude Sonnet</h3>
              <p className="text-gray-300 text-sm">
                Advanced language model specialized in reviewing change requests against EchoStar policies, providing detailed analysis and recommendations.
              </p>
            </div>
            
            <div className="bg-orange-accent/10 border border-orange-accent/40 rounded-lg p-4">
              <h3 className="text-orange-accent font-bold mb-2">Amazon Titan Premier</h3>
              <p className="text-gray-300 text-sm">
                Enterprise-grade AI model designed for comprehensive log analysis, monitoring system health, and detecting anomalies.
              </p>
            </div>
          </div>
          
          <div className="bg-purple-accent/10 border-l-4 border-purple-accent rounded p-4">
            <p className="text-purple-accent font-semibold">
              <strong>Enterprise Focus:</strong> Both models are enterprise-grade AI systems specifically designed for complex business operations, delivering the advanced AI infrastructure required for enterprise-scale implementations.
            </p>
          </div>
        </div>

        {/* Training Sources */}
        <div className="bg-dark-card border border-cyan-accent/40 rounded-lg p-6 mb-6"
             style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.15)' }}>
          <h2 className="text-2xl font-bold text-cyan-accent mb-4 flex items-center gap-2">
            <FileText size={24} />
            AI Training Sources & Documentation
          </h2>
          <p className="text-gray-300 mb-4">
            The Project VISION AI system has been trained using official EchoStar policy documents:
          </p>
          
          <div className="space-y-3">
            <a href="https://sites.google.com/dish.com/changeenablement/home?authuser=0" 
               target="_blank" 
               rel="noopener noreferrer"
               className="flex items-start gap-3 p-4 bg-cyan-accent/10 border border-cyan-accent/40 rounded-lg hover:bg-cyan-accent/20 transition-all">
              <ExternalLink size={20} className="text-cyan-accent mt-1 flex-shrink-0" />
              <div>
                <p className="text-cyan-accent font-semibold">Change Management Policy Documentation</p>
                <p className="text-gray-400 text-sm">Comprehensive change management policies and procedures</p>
              </div>
            </a>
            
            <a href="https://sites.google.com/dish.com/infosec/security-resources/security-policies-standards-and-procedures?authuser=0" 
               target="_blank" 
               rel="noopener noreferrer"
               className="flex items-start gap-3 p-4 bg-lime-accent/10 border border-lime-accent/40 rounded-lg hover:bg-lime-accent/20 transition-all">
              <ExternalLink size={20} className="text-lime-accent mt-1 flex-shrink-0" />
              <div>
                <p className="text-lime-accent font-semibold">Security and Compliance Policy Documentation</p>
                <p className="text-gray-400 text-sm">Information security policies, standards, and procedures</p>
              </div>
            </a>
          </div>
        </div>

        {/* EchoStar AI Governance Link */}
        <div className="bg-dark-card border border-orange-accent/40 rounded-lg p-6 mb-6 text-center"
             style={{ boxShadow: '0 0 30px rgba(255, 107, 53, 0.15)' }}>
          <h2 className="text-2xl font-bold text-orange-accent mb-4">EchoStar AI Governance Framework</h2>
          <p className="text-gray-300 mb-4">
            For comprehensive information about EchoStar's AI governance policies, standards, and legal framework, visit the official AI Governance portal.
          </p>
          <a href="https://hop.dish.com/home/ls/community/artificial-intelligence-ai#legal" 
             target="_blank" 
             rel="noopener noreferrer"
             className="inline-flex items-center gap-2 px-6 py-3 bg-orange-accent text-black font-bold rounded-lg hover:bg-orange-accent/80 transition-all"
             style={{ boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)' }}>
            <ExternalLink size={20} />
            EchoStar AI Governance Portal
          </a>
        </div>

        {/* Disclaimer */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6 text-center">
          <p className="text-xs text-gray-500 leading-relaxed">
            INTERNAL USE ONLY - AUTHORIZED PERSONNEL ONLY: Project VISION (Validation & Insight System for Intelligent Operations & Notification) is a proprietary enterprise application owned and operated by Echostar Corporation exclusively for internal business operations. Access is restricted to authorized Echostar employees, contractors, and approved third parties with valid business justification. By accessing this system, users acknowledge and agree to use the system solely for authorized business purposes, maintain strict confidentiality, comply with all security policies, and understand that all system activities are monitored and logged. Violation of these terms may result in immediate access termination, disciplinary action, and potential legal action.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Information;
